import { useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { captureException } from '@/lib/sentry'

const CHUNK_ERROR_RELOAD_KEY = 'propalar:route-error-chunk-reload-at'
const CHUNK_ERROR_RELOAD_COOLDOWN_MS = 10_000

function getErrorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`.trim()
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'Erro inesperado'
}

function isChunkLoadFailure(message: string): boolean {
  return (
    message.includes('dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS')
  )
}

function tryAutoRecoverChunkError(message: string): void {
  if (!isChunkLoadFailure(message)) return

  const now = Date.now()
  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) ?? '0')

  if (now - lastReloadAt < CHUNK_ERROR_RELOAD_COOLDOWN_MS) return

  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, String(now))
  window.location.reload()
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const message = getErrorMessage(error)

  useEffect(() => {
    captureException(error, {
      area: 'router',
      app: 'web',
      boundary: 'RouteErrorBoundary',
      message,
      isRouteErrorResponse: isRouteErrorResponse(error),
    })
  }, [error, message])

  useEffect(() => {
    tryAutoRecoverChunkError(message)
  }, [message])

  const isChunkFailure = isChunkLoadFailure(message)
  const title = isChunkFailure ? 'Atualizacao detectada' : 'Algo saiu do esperado'
  const description = isChunkFailure
    ? 'Sua versao ficou desatualizada apos um deploy. Vamos recarregar para sincronizar.'
    : 'Ocorreu um erro inesperado ao carregar esta pagina.'

  return (
    <main className="min-h-screen bg-bg-base text-text-primary flex items-center justify-center px-6 py-10">
      <section className="w-full max-w-xl rounded-none border border-border-default bg-bg-elevated p-6 sm:p-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-text-tertiary">
          Erro da aplicacao
        </p>
        <h1 className="mt-3 text-2xl font-semibold">{title}</h1>
        <p className="mt-3 text-sm text-text-secondary leading-relaxed">{description}</p>
        <p className="mt-4 text-xs text-text-tertiary wrap-break-word">
          Detalhe tecnico: {message}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            className="h-10 rounded-none border border-border-default px-4 text-xs font-semibold uppercase tracking-[0.16em] hover:bg-bg-subtle"
            onClick={() => window.location.reload()}
          >
            Recarregar
          </button>
          <button
            type="button"
            className="h-10 rounded-none bg-brand-propalar-600 px-4 text-xs font-semibold uppercase tracking-[0.16em] text-white hover:brightness-95"
            onClick={() => {
              window.location.href = '/'
            }}
          >
            Ir para inicio
          </button>
        </div>
      </section>
    </main>
  )
}
